import React from 'react';
import { Link } from 'react-router-dom';
import './Mission.css';

const Mission = () => {
  const principles = [
    {
      title: 'Build software people can rely on',
      description:
        'Reliability is not a slogan. It is tests, monitoring, security reviews, and architecture that still makes sense two years later.',
    },
    {
      title: 'Meet users on every device',
      description:
        'Business happens on browsers, iPhones, and Android phones. We design experiences that feel native wherever your customers work.',
    },
    {
      title: 'Connect product to growth',
      description:
        'A great app that nobody finds is unfinished work. SEO, campaigns, and email are part of how we help products succeed after launch.',
    },
    {
      title: 'Stay current without chasing hype',
      description:
        'We adopt the latest stable .NET releases and modern mobile frameworks such as SwiftUI, Jetpack Compose, MAUI, and Flutter when they reduce risk, not when they add it.',
    },
  ];

  const commitments = [
    {
      label: 'For founders',
      text: 'A clear path from idea to shipped product, with honest scope and a stack that investors and future engineers will recognize.',
    },
    {
      label: 'For enterprises',
      text: 'Integration with existing Microsoft estates, identity providers, and compliance expectations, delivered with documentation and handover.',
    },
    {
      label: 'For product teams',
      text: 'A partner that can surge on web, mobile, or growth without forcing you to hire three separate agencies.',
    },
  ];

  return (
    <main className="mission-page">
      <section className="mission-hero page-hero section">
        <div className="container">
          <p className="eyebrow">Our Mission</p>
          <h1 className="section-title">Engineer Products That Move the Business</h1>
          <p className="section-subtitle">
            Softnova exists to help organizations ship dependable web and mobile software,
            then grow the audience around it.
          </p>
        </div>
      </section>

      <section className="mission-statement section">
        <div className="container">
          <div className="statement-card">
            <p className="eyebrow">Mission</p>
            <h2>
              To design, develop, and grow digital products that are secure, scalable, and
              genuinely useful, using Microsoft .NET for the web and the latest native and
              cross-platform technologies for iOS and Android.
            </h2>
            <p>
              We measure success by what our clients can do after we leave the room: operate
              faster, serve customers better, and reach new markets with confidence.
            </p>
          </div>
        </div>
      </section>

      <section className="mission-vision section">
        <div className="container vision-grid">
          <div className="vision-card">
            <p className="eyebrow">Vision</p>
            <h3>A trusted long-term technology partner</h3>
            <p>
              We aim to be the studio clients call when the work matters: a customer portal
              that cannot go down, a field app used every day, a launch that needs both
              engineering excellence and a growth plan.
            </p>
          </div>
          <div className="vision-card">
            <p className="eyebrow">Promise</p>
            <h3>Clarity over complexity</h3>
            <p>
              You will always know what we are building, why we chose a technology, and what
              happens next. No black boxes. No surprise rewrites. No vanity metrics.
            </p>
          </div>
        </div>
      </section>

      <section className="mission-principles section">
        <div className="container">
          <h2 className="section-title">Principles We Work By</h2>
          <p className="section-subtitle">
            These beliefs shape every proposal, sprint, and campaign.
          </p>
          <div className="principles-grid">
            {principles.map((item) => (
              <article key={item.title} className="principle-card">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mission-commitments section">
        <div className="container">
          <h2 className="section-title">Who We Serve</h2>
          <div className="commitments-grid">
            {commitments.map((item) => (
              <div key={item.label} className="commitment-card">
                <h3>{item.label}</h3>
                <p>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mission-cta section">
        <div className="container">
          <div className="mission-cta-content">
            <h2>If this sounds like how you want to work, let&apos;s talk.</h2>
            <p>
              Share your product idea or current challenge. We will tell you honestly whether
              we are the right fit.
            </p>
            <Link to="/contact" className="btn btn-light" style={{ fontSize: '1.125rem', padding: '14px 40px' }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Mission;
